import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import api from "../lib/api";
import {normalizePage, type Page} from "../types/page";
import type {EmailDeliveryFailure, EmailFailureCounts, EmailResendResult} from "../types/emailFailure";

const base = "/api/v2/admin/email-failures";

/** How often the sidebar badge re-reads the count. A new failure then shows without a reload. */
const COUNT_POLL_MS = 60_000;

/**
 * One page of failures, newest first. Outstanding only by default; `includeResolved` returns every
 * row, resolved ones included, which is what the history view shows.
 */
export function useEmailFailures(includeResolved: boolean, page: number, size: number) {
    return useQuery<Page<EmailDeliveryFailure>>({
        queryKey: ["email-failures", includeResolved, page, size],
        queryFn: () =>
            api
                .get(base, {params: {page, size, includeResolved}})
                .then((r) => normalizePage<EmailDeliveryFailure>(r.data, {page, size})),
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

export function useEmailFailureCounts() {
    return useQuery<EmailFailureCounts>({
        queryKey: ["email-failure-counts"],
        queryFn: () => api.get(`${base}/count`).then((r) => r.data as EmailFailureCounts),
        staleTime: 30_000,
        refetchInterval: COUNT_POLL_MS,
    });
}

/** Marks a failure resolved without sending anything. Idempotent on an already resolved row. */
export function useDismissEmailFailure() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            api.post(`${base}/${id}/dismiss`).then((r) => r.data as EmailDeliveryFailure),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: ["email-failures"]});
            qc.invalidateQueries({queryKey: ["email-failure-counts"]});
        },
    });
}

/**
 * Sends a failed order confirmation again, to the row's recipient or to `overrideEmail`. Answers
 * once the send has been attempted, which takes a few seconds for an order with many tickets.
 */
export function useResendEmailFailure() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({id, overrideEmail}: {id: string; overrideEmail?: string}) =>
            api
                .post(`${base}/${id}/resend`, overrideEmail ? {overrideEmail} : undefined)
                .then((r) => r.data as EmailResendResult),
        onSettled: () => {
            qc.invalidateQueries({queryKey: ["email-failures"]});
            qc.invalidateQueries({queryKey: ["email-failure-counts"]});
        },
    });
}
